import { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { debounce, get } from 'lodash';

import { Entity } from 'domain/types/Entity';
import { Station } from 'domain/entities/Station';
import { useCaseSearchStation } from 'application/stations/useCases/useCaseSearchStation';
import DEFAULT_DEBOUNCE_TIME from 'infrastructure/utils/defaultDebounceTime';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { Autocomplete } from 'targets/web/components';
import { useNetworkStatus } from 'targets/web/modules/dashboard/hooks/useNetworkStatus';
import { FormikTextField } from 'targets/web/modules/dashboard/components';

import { PrimaryJobDetailsData } from './PrimaryJobDetails';

const isValueFreeSolo = (value: PrimaryJobDetailsData['station']): boolean =>
  !!value.id && !!value.name && value.id === value.name;

export const ProfitCenterAutocomplete: FC<{
  disabled?: boolean;
  stationEditable?: boolean;
  optional?: boolean;
  autoSelectIfSingle?: boolean;
  label?: string;
}> = ({ disabled, stationEditable, optional, autoSelectIfSingle, label }) => {
  const { isOffline } = useNetworkStatus();

  const { values, errors, touched, initialValues, setFieldValue } =
    useFormikContext<PrimaryJobDetailsData>();
  const t = useTranslationPrefix('jobs.details_form.primary_details');

  const [stationPhrase, setStationPhrase] = useState('');
  const [isPreselected, setIsPreselected] = useState(false);
  const { items = [], isLoading } = useCaseSearchStation({ phrase: stationPhrase });

  const stations = useMemo(() => {
    if (!initialValues.station?.id) {
      return items;
    }

    if (!items.find(({ id }) => id === initialValues.station?.id)) {
      return [initialValues.station].concat(items);
    }

    return items;
  }, [initialValues.station, items]);

  const handleInputChange = useMemo(
    () =>
      debounce((event: SyntheticEvent, newInputValue: string) => {
        if (event?.type !== 'change' && newInputValue) {
          return;
        }

        setStationPhrase(newInputValue);
      }, DEFAULT_DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    if (autoSelectIfSingle && stations.length === 1 && !isPreselected) {
      setFieldValue('station', stations[0]);
      setIsPreselected(true);
    }
  }, [autoSelectIfSingle, stations, setFieldValue, isPreselected, setIsPreselected]);

  if (isPreselected && values.station) {
    return null;
  }

  return (
    <Autocomplete<Entity<Omit<Station, 'disabled' | 'airports'>>, false, false, boolean>
      label={label || t('profit_center_label')}
      loading={isLoading}
      disabled={disabled || !stationEditable || Boolean(isPreselected && values.station)}
      freeSolo={isOffline}
      autoSelect={isOffline}
      options={stations}
      value={values.station?.id ? values.station : null}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x}
      onInputChange={handleInputChange}
      data-testname="station"
      onChange={(_, value) =>
        setFieldValue('station', typeof value === 'string' ? { name: value, id: value } : value)
      }
      renderInput={(params) => {
        const isFreeSolo = values.station && isValueFreeSolo(values.station);
        const hasError = !!get(errors, 'station') && (!!get(touched, 'station') || isFreeSolo);

        return (
          <FormikTextField
            {...params}
            name="station"
            inputProps={{
              ...params.inputProps,
              required: !optional,
            }}
            error={hasError}
            helperText={
              hasError ? (isFreeSolo && isOffline ? t('error_free_solo') : t('error')) : undefined
            }
            label={label || t('profit_center_label')}
            required={!optional}
          />
        );
      }}
    />
  );
};
